import React, { useCallback, useEffect, useState, useRef } from 'react';
import {
  View,
  Text,
  Pressable,
  StyleSheet,
  TextInput,
  SafeAreaView,
  Dimensions,
  KeyboardAvoidingView,
  Platform,
  Keyboard,
  LayoutAnimation,
  UIManager,
} from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';
import { createUser } from '../models/login-register-model';
import {
  loginUser,
  registerUser,
  updateUser,
  validateUserToken,
  getActiveCharges,
} from '../store/actions/user-actions';
import { notificationService } from '../services/notification-service';
import { IconButton } from 'react-native-paper';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import type { RootStackParamList } from '../navigation/stackTypes';
import { getIpAddressFunc } from '../components/common/utils/ip-address';
import {
  createUserSession,
  UserSessionAttributes,
} from '../models/user-session-model';
import { AppDispatch, RootState } from '../store';
import { useDispatch, useSelector } from 'react-redux';
import { setSession, clearSession } from '../store/actions/user-session';
import AsyncStorage from '@react-native-async-storage/async-storage';
import RoleService from '../services/role-service';
import {
  getUserSession,
} from '../services/user-session-service';
import { storeUserSession } from '../services/helpers/session-helpers';
import LoadingButton from '../components/LoadingButton';
import { isSuccessful } from '../services/api-requests';

const { width, height } = Dimensions.get('window');

type Props = NativeStackScreenProps<RootStackParamList, 'CreateAccount'>;
interface CreateAccountFormProps {
  formEmail?: string;
  navigation?: any;
  handleCreateAccount?: () => void;
  setNewOrder?: (newOrder: any) => void;
  orderId?: string;
  transferOrderId?: string; // For ChargeSummary: transfer completed order to new user
}

const CreateAccountForm: React.FC<CreateAccountFormProps> = ({
  formEmail,
  navigation,
  handleCreateAccount,
  setNewOrder,
  orderId,
  transferOrderId,
}) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: formEmail ?? '',
    confirmEmail: '',
    password: '',
    confirmPassword: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isPasswordVisible, setPasswordVisible] = useState(false);
  const [isPasswordVisibleConfirm, setPasswordVisibleConfirm] = useState(false);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  // Disable automatic layout animations for this component to prevent excessive callbacks
  useEffect(() => {
    if (Platform.OS === 'android') {
      // Disable LayoutAnimation for Android
      if ((UIManager as any).setLayoutAnimationEnabledExperimental) {
        (UIManager as any).setLayoutAnimationEnabledExperimental(false);
      }
    }
    
    return () => {
      // Re-enable on unmount if needed
      if (Platform.OS === 'android' && (UIManager as any).setLayoutAnimationEnabledExperimental) {
        (UIManager as any).setLayoutAnimationEnabledExperimental(true);
      }
    };
  }, []);

  // Memoize password visibility toggles to prevent re-renders
  const togglePasswordVisibility = useCallback(() => {
    setPasswordVisible((prev) => !prev);
  }, []);

  const togglePasswordVisibilityConfirm = useCallback(() => {
    setPasswordVisibleConfirm((prev) => !prev);
  }, []);

  const session: UserSessionAttributes | undefined = useSelector(
    (state: RootState) => state.user.session
  );

  const dispatch: AppDispatch = useDispatch();

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateDynamicForm = (
    updatedFormData: typeof formData
  ): Record<string, string> => {
    const newErrors: Record<string, string> = {};

    // Check first name
    if (!updatedFormData.firstName || updatedFormData.firstName.trim() === '') {
      newErrors.firstName = 'Please enter your first name.';
    }

    // Check last name
    if (!updatedFormData.lastName || updatedFormData.lastName.trim() === '') {
      newErrors.lastName = 'Please enter your last name.';
    }

    // Check email
    if (!updatedFormData.email || updatedFormData.email.trim() === '') {
      newErrors.email = 'Please enter your email address.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(updatedFormData.email)) {
      newErrors.email = 'Please enter a valid email address.';
    }

    // Check confirm email
    if (
      !updatedFormData.confirmEmail ||
      updatedFormData.confirmEmail.trim() === ''
    ) {
      newErrors.confirmEmail = 'Please confirm your email address.';
    } else if (updatedFormData.email !== updatedFormData.confirmEmail) {
      newErrors.confirmEmail = 'Email addresses do not match.';
    }

    // Check password
    if (!updatedFormData.password || updatedFormData.password.trim() === '') {
      newErrors.password = 'Please enter a password.';
    } else if (updatedFormData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters long.';
    }

    // Check confirm password
    if (
      !updatedFormData.confirmPassword ||
      updatedFormData.confirmPassword.trim() === ''
    ) {
      newErrors.confirmPassword = 'Please confirm your password.';
    } else if (updatedFormData.password !== updatedFormData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match.';
    }

    return newErrors;
  };

  const newUserSession = async (newUserData: any): Promise<void> => {
    try {
      if (newUserData?.status === 201) {
        notificationService.sendNotification(
          'success',
          'Success! Your account is registered'
        );

        // Use the registration credentials to log in and get a proper session
        const getIpAddress = await getIpAddressFunc();
        const loginObj = createUserSession({
          email: formData.email,
          username: formData.email,
          password: formData.password,
          IpAddress: getIpAddress || '1.1.1.1',
        });

        // Login with the new credentials to get a proper session
        const resp = await loginUser(loginObj);
        if(resp) {
          const getSessionData = await validateUserToken();
          const sessionData = getSessionData.payload;
          if (sessionData && sessionData.access_token && sessionData.IpAddress) {
            dispatch(setSession(sessionData));
            await storeUserSession(sessionData);
            
            // If we have an orderId, reassign it to the new user and update payment sheet
            if (orderId && setNewOrder) {
              console.log('Reassigning order to newly created user:', orderId);
              const { reassignOrder } = require('../services/orders-service');
              const newOrder = await reassignOrder(orderId);
              if (newOrder && newOrder.payload) {
                console.log('Order reassigned successfully, updating payment sheet');
                setNewOrder(newOrder.payload);
              }
            }
            
            handleCreateAccount?.();
            
            // Only navigate if navigation prop is provided (standalone form)
            // If navigation is undefined, we're in ChargerStation context - stay on current screen
            if (navigation) {
              navigation.navigate('MyAccount');
            }
          } else {
            notificationService.sendNotification(
              'error',
              'Registration successful but login failed. Please try logging in manually.'
            );
          }
        }
      } else {
        //notificationService.sendNotification('error', 'There was an issue while registering, please try again');
      }
    } catch (error) {
      console.error('Error in newUserSession:', error);
      notificationService.sendNotification(
        'error',
        'An unexpected error occurred during login.'
      );
    }
  };

  useEffect(() => {
    const verifySession = async () => {
      const getSessionVerify = await validateUserToken();
      const sessionVerify = getSessionVerify.payload;
      if (!isSuccessful(getSessionVerify.status) || !sessionVerify) {
        AsyncStorage.clear();
      } else {
        await AsyncStorage.setItem('token', sessionVerify.access_token);
        await AsyncStorage.setItem('IpAddress', sessionVerify.IpAddress!);
        await AsyncStorage.setItem('email', sessionVerify.email);
        // await AsyncStorage.setItem('role', JSON.stringify(sessionVerify.role));
        await AsyncStorage.setItem('userId', sessionVerify.id);
        await storeUserSession(sessionVerify);
        newUserSession({ email: sessionVerify.email, status: 200 });
      }
    };

    if (session && !RoleService.isGuest(session)) {
      verifySession();
    }
  }, [session]);

  // Debounce validation to prevent excessive re-renders
  const validationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleInputChange = (field: string, value: string) => {
    // Update form data immediately for responsive input
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Debounce validation - only run after user stops typing for 300ms
    if (hasAttemptedSubmit) {
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current);
      }
      
      validationTimeoutRef.current = setTimeout(() => {
        setFormData((currentFormData) => {
          const updatedErrors = validateDynamicForm(currentFormData);
          setErrors(updatedErrors);
          return currentFormData;
        });
      }, 300);
    }
  };

  // Validate a specific field on blur (when user leaves the field)
  const handleFieldBlur = (field: string) => {
    if (hasAttemptedSubmit) {
      const updatedErrors = validateDynamicForm(formData);
      setErrors(updatedErrors);
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current);
      }
    };
  }, []);

  const signUp = async (): Promise<boolean> => {
    try {
      // ChargeSummary flow: DON'T clear guest session yet (need token for transferOrder)
      // Other flows: Clear existing guest session before registration
      if (RoleService.isGuest(session) && !transferOrderId) {
        try {
          console.log('Clearing guest session before registration (not ChargeSummary flow)');
          await dispatch(clearSession(session));
          await AsyncStorage.clear();
        } catch (clearError) {
          console.error('Error clearing guest session:', clearError);
          // Continue with registration even if clearing fails
        }
      } else if (transferOrderId) {
        console.log('ChargeSummary flow: Preserving guest session for transferOrder');
      }

      const registerObj = createUser({
        email: formData.email,
        username: formData.email,
        password: formData.password,
        first_name: formData.firstName,
        last_name: formData.lastName,
        service: 'meander',
      });

      // Always register, then handle context-specific flow
      const resp = await registerUser(registerObj);

      if (resp?.errObj) {
        notificationService.sendNotification('error', resp.errObj.message);
        // If registration fails, ensure user is still signed in as guest
        try {
          const guestSession = await getUserSession();
          if (guestSession && RoleService.isGuest(guestSession)) {
            dispatch(setSession(guestSession));
          }
        } catch (guestError) {
          console.error('Error restoring guest session:', guestError);
        }
        return false; // Return false if registration fails.
      }

      // ChargeSummary flow: Transfer order BEFORE logging in (while still guest)
      if (transferOrderId && resp?.uuid) {
        console.log('Transferring completed order to new user (while still guest):', transferOrderId);
        
        // Verify guest token is available for transferOrder
        const guestToken = await AsyncStorage.getItem('token');
        if (!guestToken) {
          console.error('ERROR: Guest token missing! transferOrder will fail without authorization.');
          notificationService.sendNotification(
            'warning',
            'Could not transfer order history. Please contact support if needed.'
          );
        } else {
          console.log('Guest token confirmed, proceeding with transferOrder using guest auth');
          try {
            const { transferOrder } = require('../services/orders-service');
            const transferResult = await transferOrder(transferOrderId, resp.uuid);
            if (transferResult && transferResult.payload) {
              console.log('Order transferred successfully to new user');
            } else {
              console.warn('Order transfer may have failed, but continuing with login');
            }
          } catch (transferError) {
            console.error('Error transferring order:', transferError);
            // Continue with login even if transfer fails
          }
        }
      }

      // Registration succeeded, now login
      const getIpAddress = await getIpAddressFunc();
      const loginObj = createUserSession({
        email: formData.email,
        username: formData.email,
        password: formData.password,
        IpAddress: getIpAddress || '1.1.1.1',
      });
      const loginResp = await loginUser(loginObj);
        if(loginResp){
          const getSessionData = await validateUserToken();
          const sessionData = getSessionData.payload;
        if (sessionData && sessionData.access_token && sessionData.IpAddress) {
          dispatch(setSession(sessionData));
          await storeUserSession(sessionData);
          
          // Fetch active charges after successful login (optional for new accounts)
          try {
            await dispatch(getActiveCharges());
          } catch (chargeError) {
            // New accounts might not have active charges - this is expected
            console.log('No active charges for new account (expected):', chargeError);
          }
          
          // ChargerStation flow: Reassign active order and update payment sheet
          if (orderId && setNewOrder && !transferOrderId) {
            console.log('Reassigning active order to newly created user:', orderId);
            const { reassignOrder } = require('../services/orders-service');
            const newOrder = await reassignOrder(orderId);
            if (newOrder && newOrder.payload) {
              console.log('Order reassigned successfully, updating payment sheet');
              setNewOrder(newOrder.payload);
            }
          }
          
          handleCreateAccount?.();
          
          // Only navigate if navigation prop is provided (standalone form)
          // If navigation is undefined, we're in embedded context - stay on current screen
          if (navigation) {
            navigation.navigate('MyAccount');
          }
          return true;
        } else {
          notificationService.sendNotification(
            'error',
            'Registration successful but login failed. Please try logging in manually.'
          );
          // If login fails, restore guest session
          try {
            const guestSession = await getUserSession();
            if (guestSession && RoleService.isGuest(guestSession)) {
              dispatch(setSession(guestSession));
            }
          } catch (guestError) {
            console.error(
              'Error restoring guest session after login failure:',
              guestError
            );
          }
          return false;
        }
      } else {
        // Login failed
        notificationService.sendNotification(
          'error',
          'Registration successful but login failed. Please try logging in manually.'
        );
        return false;
      }
      return false; // Should never reach here, but TypeScript wants it
    } catch (error) {
      console.error('Error during signup:', error);
      // If any error occurs, ensure user is still signed in as guest
      try {
        const guestSession = await getUserSession();
        if (guestSession && RoleService.isGuest(guestSession)) {
          dispatch(setSession(guestSession));
        }
      } catch (guestError) {
        console.error('Error restoring guest session after error:', guestError);
      }
      return false; // Return false if an exception occurs.
    }
  };

  // Check if form has validation errors
  const hasValidationErrors = (): boolean => {
    const validationErrors = validateDynamicForm(formData);
    return Object.keys(validationErrors).length > 0;
  };

  // Check if all required fields are filled
  const isFormComplete = (): boolean => {
    return (
      formData.firstName.trim() !== '' &&
      formData.lastName.trim() !== '' &&
      formData.email.trim() !== '' &&
      formData.confirmEmail.trim() !== '' &&
      formData.password.trim() !== '' &&
      formData.confirmPassword.trim() !== ''
    );
  };

  // Check if button should be disabled (only disable if form is incomplete, not for validation errors)
  const isButtonDisabled = (): boolean => {
    return !isFormComplete();
  };

  const handleSubmit = async () => {
    Keyboard.dismiss();
    // Set flag to show validation errors
    setHasAttemptedSubmit(true);

    // Run validation and show errors if any
    const validationErrors = validateDynamicForm(formData);
    setErrors(validationErrors);

    // If there are validation errors, don't proceed with signup
    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    try {
      const signupSuccess = await signUp();
      if (!signupSuccess) {
        notificationService.sendNotification(
          'error',
          'Signup failed. Please try again.'
        );
      }
      // Navigation is now handled in signUp() for both guest and new user flows
    } catch (error) {
      console.error('Error during signup:', error);
      notificationService.sendNotification(
        'error',
        'An error occurred during signup. Please try again.'
      );
    }
  };

  // Determine if we're in standalone mode (full screen) vs embedded mode (ChargerStation)
  // Standalone mode = has navigation prop
  // Embedded mode = has handleCreateAccount callback (no navigation)
  const isStandaloneMode = !!navigation;

  // Render form content (shared between both modes)
  const renderFormContent = () => (
    <View className="p-4">
              <Text className="text-2xl font-bold text-center text-white">
                Create an account
              </Text>
              <Text
                style={styles.subHeaderText}
                className="mb-5 text-base font-normal text-center"
              >
                You will be able to log in with your email in the future, using
                the password you set below.
              </Text>

              <View>
                <Text className="pt-3 pb-2 text-base font-semibold text-white">
                  First Name
                </Text>
                <TextInput
                  placeholder="Enter first name"
                  placeholderTextColor={'#8D8E90'}
                  style={[styles.BgColor, { borderRadius: 25 }]}
                  className="p-4 w-full text-white"
                  value={formData.firstName}
                  onChangeText={(value: string) =>
                    handleInputChange('firstName', value)
                  }
                  onBlur={() => handleFieldBlur('firstName')}
                />
                <Text className="pt-3 pb-2 text-base font-semibold text-white">
                  Last Name
                </Text>
                <TextInput
                  placeholder="Enter last name"
                  placeholderTextColor={'#8D8E90'}
                  style={[styles.BgColor, { borderRadius: 25 }]}
                  className="p-4 w-full text-white"
                  value={formData.lastName}
                  onChangeText={(value: string) =>
                    handleInputChange('lastName', value)
                  }
                  onBlur={() => handleFieldBlur('lastName')}
                />
                {hasAttemptedSubmit && errors.firstName ? (
                  <Text style={styles.errorText}>{errors.firstName}</Text>
                ) : null}
                {hasAttemptedSubmit && errors.lastName ? (
                  <Text style={styles.errorText}>{errors.lastName}</Text>
                ) : null}

                <Text className="pt-3 pb-2 text-base font-semibold text-white">
                  Email
                </Text>
                <TextInput
                  placeholder="Enter your email"
                  placeholderTextColor={'#8D8E90'}
                  style={[styles.BgColor, { borderRadius: 25 }]}
                  className="p-4 w-full text-white"
                  value={formData.email}
                  autoCorrect={false}
                  autoComplete="email"
                  textContentType="none"
                  onChangeText={(value) => handleInputChange('email', value)}
                  onBlur={() => handleFieldBlur('email')}
                />
                {hasAttemptedSubmit && errors.email ? (
                  <Text style={styles.errorText}>{errors.email}</Text>
                ) : null}

                <Text className="pt-3 pb-2 text-base font-semibold text-white">
                  Confirm Email
                </Text>
                <TextInput
                  placeholder="Confirm your email"
                  placeholderTextColor={'#8D8E90'}
                  style={[styles.BgColor, { borderRadius: 25 }]}
                  className="p-4 w-full text-white"
                  value={formData.confirmEmail}
                  autoCorrect={false}
                  autoComplete="off"
                  textContentType="none"
                  onChangeText={(value) =>
                    handleInputChange('confirmEmail', value)
                  }
                  onBlur={() => handleFieldBlur('confirmEmail')}
                />
                {hasAttemptedSubmit && errors.confirmEmail ? (
                  <Text style={styles.errorText}>{errors.confirmEmail}</Text>
                ) : null}

                <View>
                  <Text className="pt-3 pb-2 text-base font-semibold text-white">
                    Create a Password
                  </Text>
                  <TextInput
                    placeholder="Enter your password"
                    placeholderTextColor={'#8D8E90'}
                    style={[styles.BgColor, { borderRadius: 25 }]}
                    className="p-4 w-full text-white"
                    secureTextEntry={!isPasswordVisible}
                    value={formData.password}
                    autoCorrect={false}
                    autoComplete="new-password"
                    textContentType="none"
                    onChangeText={(value) =>
                      handleInputChange('password', value)
                    }
                    onBlur={() => handleFieldBlur('password')}
                  />
                  <IconButton
                    icon={(props) => (
                      <MaterialCommunityIcons
                        name={isPasswordVisible ? 'eye' : 'eye-off'}
                        {...props}
                        style={{ color: '#01556F' }}
                      />
                    )}
                    onPress={togglePasswordVisibility}
                    style={styles.createPasswordIcon}
                  />
                </View>

                {hasAttemptedSubmit && errors.password ? (
                  <Text style={styles.errorText}>{errors.password}</Text>
                ) : null}

                <Text className="pt-3 pb-2 text-base font-semibold text-white">
                  Confirm Password
                </Text>
                <View>
                  <TextInput
                    placeholder="Confirm your password"
                    placeholderTextColor={'#8D8E90'}
                    style={[styles.BgColor, { borderRadius: 25 }]}
                    className="p-4 w-full text-white"
                    secureTextEntry={!isPasswordVisibleConfirm}
                    value={formData.confirmPassword}
                    autoCorrect={false}
                    autoComplete="off"
                    textContentType="none"
                    onChangeText={(value) =>
                      handleInputChange('confirmPassword', value)
                    }
                    onBlur={() => handleFieldBlur('confirmPassword')}
                  />
                  <IconButton
                    icon={(props) => (
                      <MaterialCommunityIcons
                        name={isPasswordVisibleConfirm ? 'eye' : 'eye-off'}
                        {...props}
                        style={{ color: '#01556F' }}
                      />
                    )}
                    onPress={togglePasswordVisibilityConfirm}
                    style={styles.createPasswordIconConfirm}
                  />
                </View>
                {hasAttemptedSubmit && errors.confirmPassword ? (
                  <Text style={styles.errorText}>{errors.confirmPassword}</Text>
                ) : null}

                {/* <Pressable
                                onPress={handleSubmit}
                                style={styles.button}
                                className='p-4 mt-5 text-center rounded-full'>
                                <Text className='text-lg font-medium text-center text-white'>Create my account</Text>
                            </Pressable> */}
                <View style={{ marginTop: 20 }}>
                  <LoadingButton
                    onPress={handleSubmit}
                    disabled={isButtonDisabled()}
                    className="p-4 mt-5 text-center rounded-full"
                    backgroundColor={styles.button.backgroundColor}
                  >
                    Create My Account
                  </LoadingButton>
                </View>
              </View>
            </View>
  );

  // Standalone mode: Full screen with SafeAreaView
  if (isStandaloneMode) {
    return (
      <SafeAreaView style={{ backgroundColor: '#1A1D21', flex: 1 }}>
        <KeyboardAvoidingView
          style={{ flex: 1 }}
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}
          keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : 0}
        >
          <ScrollView
            keyboardShouldPersistTaps="handled"
            contentContainerStyle={{ flexGrow: 1 }}
            style={styles.containerBgColor}
          >
            {renderFormContent()}
          </ScrollView>
        </KeyboardAvoidingView>
      </SafeAreaView>
    );
  }

  // Embedded mode: No SafeAreaView - just the form content
  return (
    <View style={styles.containerBgColor}>
      <ScrollView
        keyboardShouldPersistTaps="handled"
        contentContainerStyle={{ flexGrow: 1 }}
        nestedScrollEnabled={true}
      >
        {renderFormContent()}
      </ScrollView>
    </View>
  );
};

export default CreateAccountForm;

const styles = StyleSheet.create({
  containerBgColor: {
    backgroundColor: '#1A1D21',
  },
  subHeaderText: {
    color: '#8D8E90',
  },
  BgColor: {
    backgroundColor: '#292D33',
  },
  errorText: {
    color: '#f38787',
    fontSize: 14,
    marginTop: 4,
  },
  button: {
    backgroundColor: '#39BEB7',
  },
  createPasswordIcon: {
    position: 'absolute',
    top: 45,
    alignSelf: 'flex-end',
  },
  createPasswordIconConfirm: {
    position: 'absolute',
    alignSelf: 'flex-end',
  },
});
